/**
 * Note/folder helpers and pure state transitions.
 *
 * All the ordering and derivation rules here mirror `NoteStore.swift`:
 * - folders: `sortOrder ?? Int.max`, then localized name compare
 * - notes & vault: `updatedAt` descending
 * - trash: `deletedAt` descending
 * - empty titles commit as "Untitled"
 */

import type { FolderRecord, NoteRecord } from '../sync/dto.js';

export const UNTITLED = 'Untitled';

export type SelectionKind = 'notes' | 'trash' | 'vault';

export interface LibraryState {
  folders: FolderRecord[];
  notes: NoteRecord[];
}

/** `localizedStandardCompare` equivalent for folder names. */
const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function folderOrdered(a: FolderRecord, b: FolderRecord): number {
  const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return nameCollator.compare(a.name, b.name);
}

/** Active (non-trashed, non-vaulted) notes, newest first. */
export function activeNotes(notes: NoteRecord[]): NoteRecord[] {
  return notes
    .filter((note) => !note.deletedAt && !note.isVaulted)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

/** Vaulted notes, newest first. */
export function vaultNotes(notes: NoteRecord[]): NoteRecord[] {
  return notes
    .filter((note) => note.isVaulted && !note.deletedAt)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

/** Trashed notes, most recently deleted first. */
export function trashedNotes(notes: NoteRecord[]): NoteRecord[] {
  return notes
    .filter((note) => Boolean(note.deletedAt) && !note.isVaulted)
    .sort((a, b) => (b.deletedAt?.getTime() ?? 0) - (a.deletedAt?.getTime() ?? 0));
}

export function notesInFolder(notes: NoteRecord[], folderId: string | null): NoteRecord[] {
  return activeNotes(notes).filter((note) => (note.folderId ?? null) === folderId);
}

export interface FolderNode {
  folder: FolderRecord;
  children: FolderNode[];
}

/** Builds the sidebar tree from the flat folder list. */
export function buildFolderTree(folders: FolderRecord[]): FolderNode[] {
  const byParent = new Map<string | null, FolderRecord[]>();
  for (const folder of folders) {
    const key = folder.parentId ?? null;
    const list = byParent.get(key) ?? [];
    list.push(folder);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) list.sort(folderOrdered);

  function build(parentId: string | null): FolderNode[] {
    return (byParent.get(parentId) ?? []).map((folder) => ({
      folder,
      children: build(folder.id),
    }));
  }

  return build(null);
}

/** Every folder id in the subtree rooted at `id`, including `id` itself. */
export function folderSubtreeIds(folders: FolderRecord[], id: string): string[] {
  const childrenOf = new Map<string | null, string[]>();
  for (const folder of folders) {
    const key = folder.parentId ?? null;
    const list = childrenOf.get(key) ?? [];
    list.push(folder.id);
    childrenOf.set(key, list);
  }

  const result: string[] = [];
  const queue = [id];
  const seen = new Set<string>();
  while (queue.length) {
    const current = queue.shift()!;
    if (seen.has(current)) continue;
    seen.add(current);
    result.push(current);
    queue.push(...(childrenOf.get(current) ?? []));
  }
  return result;
}

/** Folders that would create a cycle if `id` were moved under them. */
export function descendantIds(folders: FolderRecord[], id: string): Set<string> {
  const ids = folderSubtreeIds(folders, id);
  return new Set(ids.filter((candidate) => candidate !== id));
}

export function sanitizeTitle(raw: string): string {
  // Titles are single-line: collapse newlines and trim, then fall back.
  const collapsed = raw.replace(/\s*\n+\s*/g, ' ').trim();
  return collapsed.length ? collapsed : UNTITLED;
}

/** Derives a title from the first non-empty line of the body. */
export function titleFromContent(content: string): string {
  const line = content.split('\n').find((candidate) => candidate.trim().length > 0);
  if (!line) return UNTITLED;
  // Strip leading markdown heading markers and list bullets.
  const cleaned = line.replace(/^\s*#{1,6}\s*/, '').replace(/^\s*[-*+]\s+/, '').trim();
  return cleaned.length ? cleaned : UNTITLED;
}

export function isUntitled(note: NoteRecord): boolean {
  return note.title.trim() === '' || note.title === UNTITLED;
}

/** Preview text: the body after the title line. */
export function notePreview(content: string): string {
  const lines = content.split('\n');
  let index = 0;
  while (index < lines.length && lines[index]!.trim() === '') index += 1;
  // Skip the title line itself.
  index += 1;
  while (index < lines.length && lines[index]!.trim() === '') index += 1;
  const rest = lines.slice(index).join(' ').trim();
  const stripped = rest
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_`>]/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length ? stripped : 'No additional text';
}

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Soft-deletes a note: sets `deletedAt`/`trashedFrom`, bumps `updatedAt` so the
 * change propagates as newer than any untouched remote copy.
 */
export function trashNote(
  note: NoteRecord,
  folderNames: Map<string, string>,
  now = new Date(),
): NoteRecord {
  return {
    ...note,
    deletedAt: now,
    trashedFrom: note.folderId ? folderNames.get(note.folderId) ?? null : null,
    updatedAt: now,
  };
}

export function restoreNote(note: NoteRecord, now = new Date()): NoteRecord {
  return { ...note, deletedAt: null, trashedFrom: null, updatedAt: now };
}

/**
 * Moves every note in a folder subtree to the trash — mirrors `deleteFolder`
 * in NoteStore.swift. Callers queue the removed folders for hard delete.
 */
export function deleteFolder(
  state: LibraryState,
  id: string,
  now = new Date(),
): { notes: NoteRecord[]; folderIds: string[] } {
  const ids = new Set(folderSubtreeIds(state.folders, id));
  const folderNames = new Map(
    state.folders.filter((folder) => ids.has(folder.id)).map((folder) => [folder.id, folder.name]),
  );

  const notes = state.notes.map((note) =>
    note.folderId && ids.has(note.folderId) ? trashNote(note, folderNames, now) : note,
  );

  return { notes, folderIds: [...ids] };
}
